import React from 'react';
import Icon from '../AppIcon';

const SessionProgressIndicator = ({ 
  isVisible = false,
  currentQuestion = 1,
  totalQuestions = 10,
  timeRemaining = 1800, // 30 minutes in seconds
  sessionType = 'practice',
  onPause,
  onStop
}) => {
  if (!isVisible) return null;

  const progressPercentage = (currentQuestion / totalQuestions) * 100;
  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;

  const formatTime = (mins, secs) => {
    return `${mins?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const getSessionTypeColor = () => {
    switch (sessionType) {
      case 'live':
        return 'text-red-400 border-red-500/20 bg-red-500/10';
      case 'practice':
        return 'text-purple-400 border-purple-500/20 bg-purple-500/10';
      case 'mock':
        return 'text-indigo-400 border-indigo-500/20 bg-indigo-500/10';
      default:
        return 'text-purple-400 border-purple-500/20 bg-purple-500/10';
    }
  };

  return (
    <div className="sticky  left-0 right-0 z-40 bg-gray-900/95 backdrop-blur border-b border-gray-700 animate-fade-in">
      <div className="flex items-center justify-between px-6 py-3">
        {/* Session Info */}
        <div className="flex items-center space-x-4">
          <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium border ${getSessionTypeColor()}`}>
            <div className="w-2 h-2 rounded-full bg-current animate-pulse" />
            <span className="capitalize">{sessionType} Session</span>
          </div>
          
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <Icon name="MessageSquare" size={16} />
            <span>Question {currentQuestion} of {totalQuestions}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="flex-1 max-w-md mx-6">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-400">Progress</span>
            <span className="text-xs text-gray-400">{Math.round(progressPercentage)}%</span>
          </div>
          <div className="w-full bg-gray-800/50 rounded-full h-2 border border-gray-700/50">
            <div 
              className="bg-gradient-to-r from-purple-500 to-indigo-600 h-2 rounded-full transition-all duration-300 ease-smooth"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Timer and Controls */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-sm">
            <Icon 
              name="Clock" 
              size={16} 
              className={timeRemaining < 300 ? 'text-yellow-400' : 'text-gray-400'} 
            />
            <span className={`font-mono ${timeRemaining < 300 ? 'text-yellow-400' : 'text-white'}`}>
              {formatTime(minutes, seconds)}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={onPause}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-800/50 hover:bg-gray-700/70 text-gray-400 hover:text-white transition-all duration-150 border border-gray-700/50 hover:border-gray-600"
              title="Pause session"
            >
              <Icon name="Pause" size={16} />
            </button>
            
            <button
              onClick={onStop}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 hover:text-red-300 transition-all duration-150 border border-red-500/30 hover:border-red-500/50"
              title="End session"
            >
              <Icon name="Square" size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden px-4 pb-3">
        <div className="flex items-center justify-between mb-2">
          <div className={`flex items-center space-x-2 px-2 py-1 rounded-full text-xs font-medium border ${getSessionTypeColor()}`}>
            <div className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
            <span className="capitalize">{sessionType}</span>
          </div>
          
          <div className="flex items-center space-x-3">
            <span className="text-xs text-gray-400">
              {currentQuestion}/{totalQuestions}
            </span>
            <span className={`text-xs font-mono ${timeRemaining < 300 ? 'text-yellow-400' : 'text-white'}`}>
              {formatTime(minutes, seconds)}
            </span>
          </div>
        </div>
        
        <div className="w-full bg-gray-800/50 rounded-full h-1.5 border border-gray-700/50">
          <div 
            className="bg-gradient-to-r from-purple-500 to-indigo-600 h-1.5 rounded-full transition-all duration-300 ease-smooth"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default SessionProgressIndicator;